import { useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { TimelineAnimation } from '@/components/ui/timeline-animation'
import { Button } from '@/components/ui/Button'

interface HeroAiInfrastructureProps {
  visual?: ReactNode
  serifFont: string
}

const heroPoints = [
  'Train on text, URLs, and files',
  'Add tools, MCP, and custom skills',
  'Keep every user scoped to their own data',
]

export function HeroAiInfrastructure({
  visual,
  serifFont,
}: HeroAiInfrastructureProps) {
  const timelineRef = useRef<HTMLDivElement | null>(null)

  return (
    <section
      ref={timelineRef}
      className="relative overflow-hidden bg-[#0a2b29] px-4 pb-20 pt-36 text-white sm:px-6 lg:px-8 lg:pb-28 lg:pt-40"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(111,196,163,0.18),transparent_38%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(126,191,169,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(126,191,169,0.06)_1px,transparent_1px)] bg-[size:84px_84px] [mask-image:radial-gradient(circle_at_center,black,transparent_78%)]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mx-auto max-w-5xl text-center">
          <TimelineAnimation
            timelineRef={timelineRef}
            as="h1"
            animationNum={1}
            className="text-5xl font-semibold leading-[0.9] tracking-[-0.06em] text-white sm:text-6xl lg:text-8xl"
          >
            Give every user
            <br />
            <span style={{ fontFamily: serifFont }} className="italic text-[#8cd5b4]">
              an AI agent
            </span>
            <br />
            inside your product.
          </TimelineAnimation>

          <TimelineAnimation
            timelineRef={timelineRef}
            as="p"
            animationNum={2}
            className="mx-auto mt-8 max-w-3xl text-lg leading-8 text-white/72 sm:text-xl"
          >
            Train on their data, connect the right tools, and ship the full
            agent experience without building the infrastructure yourself.
          </TimelineAnimation>

          <TimelineAnimation
            timelineRef={timelineRef}
            animationNum={3}
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link to="/auth/register">
              <Button className="group h-14 rounded-full bg-[#7fd0ab] px-8 text-base font-semibold text-[#0a2b29] shadow-[0_24px_50px_rgba(7,41,41,0.32)] hover:bg-[#95dab8]">
                Start Free
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <a href="#compare">
              <Button
                variant="outline"
                className="h-14 rounded-full border-white/12 bg-white/6 px-8 text-base font-semibold text-white hover:bg-white/10 hover:text-white"
              >
                See how it works
              </Button>
            </a>
          </TimelineAnimation>

          <TimelineAnimation
            timelineRef={timelineRef}
            animationNum={4}
            className="mt-12 grid gap-3 text-left sm:grid-cols-3"
          >
            {heroPoints.map((point) => (
              <div
                key={point}
                className="rounded-2xl border border-white/10 bg-white/6 px-4 py-4 backdrop-blur"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#7fd0ab]/18 text-[#9fe0c2]">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <p className="text-sm leading-6 text-white/82">{point}</p>
                </div>
              </div>
            ))}
          </TimelineAnimation>
        </div>

        {visual ? (
          <TimelineAnimation timelineRef={timelineRef} animationNum={5} className="mt-16">
            {visual}
          </TimelineAnimation>
        ) : null}
      </div>
    </section>
  )
}
